import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, qs } from '../api/client'
import type { Dashboard as DashboardData, SearchResults, SellItem, SellResult } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { useAuth } from '../auth'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Badge, CopyListDialog, Empty, Notice, Pager, fmtTime, statusTone } from '../components/ui'
import { chamberResultNote } from '../lib/chamber'
import { copyText } from '../lib/clipboard'
import { firstSheetFitError, parkBatchOnFirstSheet } from '../lib/parkDraft'

const STYLE_LABEL: Record<string, string> = { NUMBER: '数字', ALPHA: '字母', MIX: '混合', REVIEW: '其他' }
// 每页上限 300 = 后端出库单次的可选上限：一页放得下，操作员就能一次把整批挑走。
const SIZE_OPTS = [50, 100, 200, 300]

export function Search() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const term = (params.get('q') ?? '').trim()
  const page = Math.max(1, Number(params.get('page') ?? '1'))
  const size = Number(params.get('size') ?? '50')
  const [q, setQ] = useState(term)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [res, setRes] = useState<SearchResults | null>(null)
  // 搜到了就要当场勾掉：不能再跳回库存页按同样的条件搜一遍。
  const [selected, setSelected] = useState<Map<number, SellItem>>(new Map())
  const [confirm, setConfirm] = useState(false)
  const [done, setDone] = useState<null | { domains: string[]; batchCode: string; parked: boolean; parkError?: string }>(null)
  const [arming, setArming] = useState(false)
  const [armError, setArmError] = useState<unknown>(null)
  const [copied, setCopied] = useState<null | 'ok' | 'fail'>(null)
  const op = useOperation<SellResult>('sell')
  // 快捷键跟看板同一份后缀：看板上没有的不出现，卖掉最后一个在库项后下次刷新就消失。
  const board = useAsync(() => api.get<DashboardData>('/api/dashboard'), [])

  // 在库永远在已售之前由后端排好（和库存页同一条排序）。前端再排一次只会让
  // 两个板块各自给出不同的一页 —— 那正是这次"四个板块四个结果"的成因。
  const rows = res?.inventory ?? []
  // 已出库的行不给勾选：卖了的就是卖了，勾上只会让「出库」按钮灰着，像坏了。
  const selectable = useMemo(() => rows.filter((d) => d.status === 'IN_STOCK'), [rows])
  const chosen = useMemo(() => [...selected.values()], [selected])
  useEffect(() => {
    setSelected((prev) => {
      let changed = false
      const next = new Map(prev)
      for (const r of rows) {
        const held = next.get(r.id)
        if (!held) continue
        if (r.status !== 'IN_STOCK') {
          next.delete(r.id)
          changed = true
        } else if (held.version !== r.version) {
          next.set(r.id, { id: r.id, domain: r.domain, version: r.version })
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [rows])
  const allPicked = selectable.length > 0 && selectable.every((d) => selected.has(d.id))

  async function load(keepSelection: boolean) {
    if (!keepSelection) setSelected(new Map())
    setConfirm(false)
    if (!term) {
      setRes(null)
      setError(null)
      return
    }
    setBusy(true)
    setError(null)
    try {
      setRes(await api.get<SearchResults>(`/api/search${qs({ q: term, page, size })}`))
    } catch (err) {
      setError(err)
      setRes(null)
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    // 换后缀、翻页都留着已勾选的。一单经常跨好几个后缀，选完才一次性上膛。
    void load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, page, size])

  function setParam(k: string, v: string) {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page' && k !== 'size') next.set('page', '1')
    setParams(next, { replace: true })
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setParam('q', q.trim())
  }

  function searchSuffix(suffix: string) {
    setQ(suffix)
    const next = new URLSearchParams(params)
    next.set('q', suffix)
    next.set('page', '1')
    setParams(next, { replace: true })
  }

  function clearQuery() {
    setQ('')
    setParam('q', '')
  }

  const toggle = (d: { id: number; domain: string; version: number }) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(d.id)) next.delete(d.id)
      else next.set(d.id, { id: d.id, domain: d.domain, version: d.version })
      return next
    })
  }

  function toggleAll() {
    setSelected((prev) => {
      const next = new Map(prev)
      // 只作用在"还能出库的"行上：已出库的没有勾选框，全选也不该把它们拉进来。
      if (allPicked) selectable.forEach((d) => next.delete(d.id))
      else selectable.forEach((d) => next.set(d.id, { id: d.id, domain: d.domain, version: d.version }))
      return next
    })
  }

  async function copySelected() {
    setCopied((await copyText(chosen.map((s) => s.domain).join('\n'))) ? 'ok' : 'fail')
  }

  async function sell() {
    const items = [...selected.values()]
    const r = await op.run((key) => api.post<SellResult>('/api/domains/sell', { items, remark: '' }, key))
    if (r) {
      setConfirm(false)
      setDone({ domains: items.map((i) => i.domain), batchCode: r.batchCode, parked: false })
      setSelected(new Map())
      board.reload()
      void load(true)
    }
  }

  async function chamber() {
    const items = [...selected.values()]
    const domains = items.map((i) => i.domain)
    setArmError(null)
    setArming(true)
    try {
      const fit = await firstSheetFitError(domains)
      if (fit) {
        setArmError(fit)
        return
      }
      const r = await op.run((key) => api.post<SellResult>('/api/domains/sell', { items, remark: '' }, key))
      if (!r) return
      let parked = true
      let parkError = ''
      try {
        await parkBatchOnFirstSheet(domains)
      } catch (e) {
        parked = false
        parkError = e instanceof Error ? e.message : '草稿没有写上'
      }
      setConfirm(false)
      setDone({ domains, batchCode: r.batchCode, parked, parkError })
      setSelected(new Map())
      board.reload()
      void load(true)
    } finally {
      setArming(false)
    }
  }

  return (
    <section>
      <header className="page-head compact">
        <h1>搜索</h1>
      </header>

      <form className="row search-bar" onSubmit={onSubmit}>
        <input
          className="grow"
          aria-label="搜索关键字：域名、片段，或 类型-长度-后缀"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="域名 / 片段 / 类型-长度-后缀（如 数字-6-com）"
          autoFocus
        />
        <button type="submit" className="primary" disabled={busy}>
          {busy ? '搜索中…' : '搜索'}
        </button>
        <button type="button" onClick={clearQuery} disabled={!q && !term}>
          清除
        </button>
      </form>
      {(board.data?.cards ?? []).length > 0 && (
        <div className="suffix-shortcuts" role="group" aria-label="按后缀搜索">
          {(board.data?.cards ?? []).map((c) => {
            const on = term.toLowerCase() === c.suffix.toLowerCase()
            return (
              <button
                key={c.suffix}
                type="button"
                aria-pressed={on}
                title={`搜索后缀 ${c.suffix}，在库 ${c.total}`}
                onClick={() => searchSuffix(c.suffix)}
              >
                {c.suffix}
              </button>
            )
          })}
        </div>
      )}
      <Notice error={error} />

      {res && (
        <>
          <div className="row spread toolbar">
            <span className="muted" role="status">
              已选 {selected.size} 项（换后缀、翻页都保留，上膛时作为一批）
            </span>
            <div className="row wrap">
              <button type="button" onClick={toggleAll} disabled={selectable.length === 0}>
                {allPicked ? '取消全选本页' : '全选本页在库项'}
              </button>
              {selected.size > 0 && (
                <button type="button" className="link" onClick={() => void copySelected()}>
                  {copied === 'ok' ? '已复制 ✓' : copied === 'fail' ? '复制失败，请手动选' : '复制所选域名'}
                </button>
              )}
              <button type="button" onClick={() => setSelected(new Map())} disabled={!selected.size}>
                清除选择
              </button>
              <button
                type="button"
                className="danger"
                disabled={!user?.canWrite || chosen.length === 0}
                onClick={() => {
                  op.newIntent()
                  setConfirm(true)
                }}
              >
                出库（标记为已售）
              </button>
            </div>
          </div>

          <div className="card">
            <h2>
              库存结果{' '}
              <span className="muted">
                共 {res.inventoryTotal} 条 · 在库 {res.inventoryInStock} · 已出库 {res.inventorySold}
                {res.inventorySold > 0 && '（已出库排在下面、不可勾选）'}
              </span>
              {res.structured && (
                <Badge>
                  结构化：{STYLE_LABEL[res.structured.style] ?? res.structured.style} · {res.structured.charCount} 位 ·{' '}
                  {res.structured.suffix}
                </Badge>
              )}
            </h2>
            {rows.length === 0 ? (
              <Empty text="库存中没有匹配项（已排除 WITHDRAWN）。" />
            ) : (
              <div className="scroll-x">
                <table className="table">
                  <thead>
                    <tr>
                      <th aria-label="全选本页可出库项">
                        <input
                          type="checkbox"
                          checked={allPicked}
                          onChange={toggleAll}
                          disabled={selectable.length === 0}
                          title={`全选/取消本页可出库的 ${selectable.length} 条`}
                          aria-label="全选本页可出库项"
                        />
                      </th>
                      <th>域名</th>
                      <th>区</th>
                      <th>类型</th>
                      <th>后缀</th>
                      <th>长度</th>
                      <th>平台</th>
                      <th>注册日</th>
                      <th>状态</th>
                      <th>注册码</th>
                      <th>入库批次</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => {
                      const canSell = d.status === 'IN_STOCK'
                      return (
                      <tr key={d.id} className={canSell ? (selected.has(d.id) ? 'picked' : '') : 'dim sold-row'}>
                        <td>
                          {canSell ? (
                            <input
                              type="checkbox"
                              checked={selected.has(d.id)}
                              onChange={() => toggle(d)}
                              aria-label={d.domain}
                            />
                          ) : (
                            <span className="sold-mark" title="已出库，不可再选">·</span>
                          )}
                        </td>
                        <td className="mono">{d.domain}</td>
                        <td>{d.zone === 'ZONE_A' ? 'A' : 'S'}</td>
                        <td title={d.style}>{STYLE_LABEL[d.style] ?? d.style}</td>
                        <td>{d.suffix}</td>
                        <td>{d.charCount}</td>
                        <td>{d.platform || '-'}</td>
                        <td>{d.registeredOn || '-'}</td>
                        <td>
                          <Badge tone={statusTone(d.status)}>{d.status}</Badge>
                          {d.everSold && d.status !== 'SOLD' && <span className="tag">曾售出</span>}
                        </td>
                        <td>{d.registerCode || '-'}</td>
                        <td>
                          <Link to={`/batches?highlight=${d.intakeBatchId}`}>#{d.intakeBatchId}</Link>
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {res.inventoryTotal > 0 && (
              <Pager
                page={page}
                size={size}
                total={res.inventoryTotal}
                onPage={(p) => setParam('page', String(p))}
                onSize={(n) => setParam('size', String(n))}
                sizeOptions={SIZE_OPTS}
              />
            )}
          </div>

          <div className="card">
            <h2>
              卖出履历{' '}
              <span className="muted">
                共 {res.historyTotal} 条
                {(res.history ?? []).length < res.historyTotal && `，已显示最近 ${(res.history ?? []).length} 条`}
              </span>
            </h2>
            {(res.history ?? []).length === 0 ? (
              <Empty text="该条件没有卖出记录。" />
            ) : (
              <div className="scroll-x">
                <table className="table">
                  <thead>
                    <tr>
                      <th>时间</th>
                      <th>域名</th>
                      <th>动作</th>
                      <th>批次</th>
                      <th>操作人</th>
                      <th>备注</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(res.history ?? []).map((h, i) => (
                      <tr key={i}>
                        <td className="muted time">{fmtTime(h.actedAt)}</td>
                        <td className="mono">{h.domain}</td>
                        <td>
                          <Badge tone={h.action === 'SOLD' ? 'sold' : 'ok'}>{h.action}</Badge>
                          {h.everSold && <span className="tag">曾售出</span>}
                        </td>
                        <td>
                          <Link to={`/batches?highlight=${h.batchId}`}>{h.batchCode}</Link>
                        </td>
                        <td>{h.actorName || '-'}</td>
                        <td className="muted">{h.remark || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <h2>
              下单记录{' '}
              <span className="muted">
                {res.orderNote || '不会改变库存'} · 共 {res.ordersTotal} 条
                {(res.orders ?? []).length < res.ordersTotal && `，已显示最近 ${(res.orders ?? []).length} 条`}
              </span>
            </h2>
            {(res.orders ?? []).length === 0 ? (
              <Empty text="没有匹配的下单记录。" />
            ) : (
              <div className="scroll-x">
                <table className="table">
                  <thead>
                    <tr>
                      <th>录入时间</th>
                      <th>买家</th>
                      <th>机构</th>
                      <th>域名一</th>
                      <th>域名二</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(res.orders ?? []).map((o) => (
                      <tr key={o.id}>
                        <td className="muted time">{fmtTime(o.createdAt)}</td>
                        <td>{o.buyer}</td>
                        <td>{o.institution || '-'}</td>
                        <td className="mono">{o.domainOne || '-'}</td>
                        <td className="mono">{o.domainTwo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {confirm && (
        <ConfirmDialog
          title="确认出库"
          description={`将把选中的 ${chosen.length} 个域名标记为已售，并写入卖出履历。撤回请使用批次页的「整批撤回」。上膛会同时把这批追加到草稿第 1 页。`}
          busy={op.busy || arming}
          error={armError ?? op.error}
          copyLabel={copied === 'ok' ? '已复制 ✓' : copied === 'fail' ? '复制失败' : '复制'}
          onCopy={() => void copySelected()}
          extraLabel={arming ? '上膛中…' : '上膛'}
          extraTitle="标记为已售，并追加到草稿第 1 页。下一批会先空一行再续。"
          onExtra={() => void chamber()}
          onConfirm={() => void sell()}
          onClose={() => {
            if (op.busy || arming) return
            setArmError(null)
            setConfirm(false)
            op.newIntent()
          }}
        >
          <ul className="chips">
            {chosen.slice(0, 30).map((s) => (
              <li key={s.id}>{s.domain}</li>
            ))}
            {chosen.length > 30 && <li className="muted">…等 {chosen.length} 个</li>}
          </ul>
        </ConfirmDialog>
      )}

      {done && (
        <CopyListDialog
          title="本次已出库域名"
          items={done.domains}
          note={
            done.parked
              ? chamberResultNote(done.batchCode, true)
              : done.parkError
                ? chamberResultNote(done.batchCode, false, done.parkError)
                : `批次 ${done.batchCode} · 一行一个，可直接粘贴到入库表或发给买家；要整批撤回就去批次页。`
          }
          onClose={() => setDone(null)}
        />
      )}
    </section>
  )
}
